import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type UserRole = 'admin' | 'moderator' | 'user' | null;
type TeamRole = Database['public']['Enums']['team_role'];
type AdminTeamRole = Database['public']['Enums']['admin_team_role'];

// Client profile from get_my_client_profile() or get_team_member_parent_client_profile()
interface ClientProfile {
  id: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  paid: boolean | null;
  force_password_change: boolean | null;
  subscription_status: string | null;
  lifecycle_stage: string | null;
  company_name: string | null;
  created_at?: string;
  updated_at?: string;
  first_login_at?: string | null;
}

// Team member profile from get_my_team_member_profile()
interface TeamMemberProfile {
  id: string;
  client_user_id: string;
  email: string;
  name: string;
  role: TeamRole;
  force_password_change: boolean | null;
  accepted_at: string | null;
  created_at: string;
}

// Admin team member profile
interface AdminTeamMemberProfile {
  id: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: AdminTeamRole;
  is_active: boolean;
  created_at: string;
}

interface AuthContextType {
  // Core auth state
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  
  // Role information
  userRole: UserRole;
  isAdmin: boolean;
  
  // Admin team information (for admin portal team members)
  isOwner: boolean;
  isAdminTeamMember: boolean;
  adminTeamRole: AdminTeamRole | null;
  adminTeamMemberProfile: AdminTeamMemberProfile | null;
  
  // Client information (works for both primary clients and team members)
  clientId: string | null;
  clientUser: ClientProfile | null;
  
  // Team member specific
  isTeamMember: boolean;
  teamMemberProfile: TeamMemberProfile | null;
  teamMemberRole: TeamRole | null;
  
  // Password change
  forcePasswordChange: boolean;
  
  // Auth functions
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata?: Record<string, string | undefined>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshClientUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientUser, setClientUser] = useState<ClientProfile | null>(null);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamMemberProfile, setTeamMemberProfile] = useState<TeamMemberProfile | null>(null);
  const [teamMemberRole, setTeamMemberRole] = useState<TeamRole | null>(null);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  
  // Admin team member state
  const [isOwner, setIsOwner] = useState(false);
  const [isAdminTeamMember, setIsAdminTeamMember] = useState(false);
  const [adminTeamRole, setAdminTeamRole] = useState<AdminTeamRole | null>(null);
  const [adminTeamMemberProfile, setAdminTeamMemberProfile] = useState<AdminTeamMemberProfile | null>(null);

  const fetchUserRole = async (userId: string): Promise<UserRole> => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }
      
      return data?.role as UserRole || null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  const sendFirstLoginNotification = async (clientProfile: ClientProfile) => {
    // Only send notification if this is the first login (first_login_at is null)
    if (clientProfile.first_login_at) {
      return; // Already logged in before, skip notification
    }
    
    try {
      // Send the notification
      await supabase.functions.invoke('send-login-notification', {
        body: {
          clientEmail: clientProfile.email,
          clientName: clientProfile.name,
          companyName: clientProfile.company_name,
          loginTime: new Date().toISOString(),
        },
      });
      
      // Mark first login time in database
      await supabase
        .from('client_users')
        .update({ first_login_at: new Date().toISOString() })
        .eq('id', clientProfile.id);
        
    } catch (error) {
      // Don't block login if notification fails
      console.error('Error sending first login notification:', error);
    }
  };

  const loadUserData = async (userId: string, currentUser: User) => {
    try {
      // 1. Fetch admin role first
      const role = await fetchUserRole(userId);
      setUserRole(role);
      
      // Check if user is the Owner (has admin role in user_roles)
      if (role === 'admin') {
        setIsOwner(true);
        setIsAdminTeamMember(false);
        setAdminTeamRole(null);
        setAdminTeamMemberProfile(null);
        const forceChange = currentUser.user_metadata?.force_password_change === true;
        setForcePasswordChange(forceChange);
        setIsLoading(false);
        return;
      }
      
      // Check if user is an admin team member
      const { data: adminTeamData, error: adminTeamError } = await supabase
        .from('admin_team_members')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (adminTeamError) {
        console.error('Error fetching admin team member:', adminTeamError);
      }
      
      if (adminTeamData) {
        // User is an admin team member
        setIsOwner(false);
        setIsAdminTeamMember(true);
        setAdminTeamRole(adminTeamData.role);
        setAdminTeamMemberProfile(adminTeamData as AdminTeamMemberProfile);
        const forceChange = currentUser.user_metadata?.force_password_change === true;
        setForcePasswordChange(forceChange);
        setIsLoading(false);
        return;
      }
      
      // Reset admin team state if not an admin team member
      setIsOwner(false);
      setIsAdminTeamMember(false);
      setAdminTeamRole(null);
      setAdminTeamMemberProfile(null);
      
      // Moderator users don't need client data either
      if (role === 'moderator') {
        const forceChange = currentUser.user_metadata?.force_password_change === true;
        setForcePasswordChange(forceChange);
        setIsLoading(false);
        return;
      }

      // 2. Check if user is a team member
      const { data: teamMemberData, error: teamError } = await supabase.rpc('get_my_team_member_profile');
      
      if (teamError) {
        console.error('Error fetching team member profile:', teamError);
      }
      
      if (teamMemberData && teamMemberData.length > 0) {
        // User is a team member
        const teamMember = teamMemberData[0] as TeamMemberProfile;
        setTeamMemberProfile(teamMember);
        setIsTeamMember(true);
        setTeamMemberRole(teamMember.role);
        setForcePasswordChange(teamMember.force_password_change ?? false);
        
        // Get parent client profile
        const { data: parentData, error: parentError } = await supabase.rpc('get_team_member_parent_client_profile');
        
        if (parentError) {
          console.error('Error fetching parent client profile:', parentError);
        }
        
        if (parentData && parentData.length > 0) {
          const parentClient = parentData[0] as ClientProfile;
          setClientUser(parentClient);
          setClientId(parentClient.id);
          // Team member first login is handled by database trigger notify_admin_team_member_first_login
        }
      } else {
        // User is a primary client (or has no client profile yet)
        setIsTeamMember(false);
        setTeamMemberProfile(null);
        setTeamMemberRole(null);
        
        const { data: clientData, error: clientError } = await supabase.rpc('get_my_client_profile');
        
        if (clientError) {
          console.error('Error fetching client profile:', clientError);
        }
        
        if (clientData && clientData.length > 0) {
          const client = clientData[0] as ClientProfile;
          setClientUser(client);
          setClientId(client.id);
          setForcePasswordChange(client.force_password_change ?? false);
          
          // Send first login notification for primary client (only on first login)
          sendFirstLoginNotification(client);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshClientUser = useCallback(async () => {
    if (!user) return;
    
    try {
      if (isTeamMember) {
        const { data, error } = await supabase.rpc('get_team_member_parent_client_profile');
        if (error) {
          console.error('Error refreshing parent client profile:', error);
          return;
        }
        if (data && data.length > 0) {
          setClientUser(data[0] as ClientProfile);
        }
      } else {
        const { data, error } = await supabase.rpc('get_my_client_profile');
        if (error) {
          console.error('Error refreshing client profile:', error);
          return;
        }
        if (data && data.length > 0) {
          setClientUser(data[0] as ClientProfile);
        }
      }
    } catch (error) {
      console.error('Error refreshing client user:', error);
    }
  }, [user, isTeamMember]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Keep loading true until data is fetched
          setIsLoading(true);
          // Invalidate account-id query to ensure fresh data after login
          queryClient.invalidateQueries({ queryKey: ['account-id'] });
          // Defer Supabase calls with setTimeout to prevent deadlocks
          setTimeout(() => {
            loadUserData(session.user.id, session.user);
          }, 0);
        } else {
          // Reset all state on logout
          setUserRole(null);
          setClientId(null);
          setClientUser(null);
          setIsTeamMember(false);
          setTeamMemberProfile(null);
          setTeamMemberRole(null);
          setForcePasswordChange(false);
          setIsOwner(false);
          setIsAdminTeamMember(false);
          setAdminTeamRole(null);
          setAdminTeamMemberProfile(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        loadUserData(session.user.id, session.user);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (
    email: string, 
    password: string, 
    metadata?: Record<string, string | undefined>
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserRole(null);
    setClientId(null);
    setClientUser(null);
    setIsTeamMember(false);
    setTeamMemberProfile(null);
    setTeamMemberRole(null);
    setForcePasswordChange(false);
    setIsOwner(false);
    setIsAdminTeamMember(false);
    setAdminTeamRole(null);
    setAdminTeamMemberProfile(null);
  };

  const isAdmin = userRole === 'admin' || userRole === 'moderator' || isAdminTeamMember;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        userRole,
        isAdmin,
        isOwner,
        isAdminTeamMember,
        adminTeamRole,
        adminTeamMemberProfile,
        clientId,
        clientUser,
        isTeamMember,
        teamMemberProfile,
        teamMemberRole,
        forcePasswordChange,
        signIn,
        signUp,
        signOut,
        refreshClientUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };
export type { AuthContextType, ClientProfile, TeamMemberProfile, AdminTeamMemberProfile };
